// @pta/types — közös típusok BE és FE között

// -------- Country / lokalizáció --------

export type CountryCode = string; // ISO 3166-1 alpha-2, pl. 'HU'

export interface NumberFormat {
  decimal: string;
  thousand: string;
  currencyPosition: 'before' | 'after';
}

export interface Country {
  code: CountryCode;
  nameKey: string;
  currency: string;
  locale: string;
  dateFormat: string;
  numberFormat: NumberFormat;
  /**
   * F2b: engedélyezett másodlagos (tájékoztató) devizák ebben az országban.
   * 3-karakteres currency kódok. Nem tartalmazhatja az elsődleges currency-t.
   */
  allowedSecondaryCurrencies: string[];
  isActive: boolean;
}

// -------- School year --------

export interface SchoolYear {
  id: number;
  countryCode: CountryCode;
  displayName: string;
  startDate: string; // ISO date, YYYY-MM-DD
  endDate: string;
  isCurrent: boolean;
}

// -------- Institution types --------

export interface InstitutionPrimaryType {
  id: number;
  countryCode: CountryCode;
  code: string; // pl. 'kindergarten', 'primary', 'secondary'
  nameKey: string;
  sortOrder: number;
  isActive: boolean;
}

export interface InstitutionSecondaryType {
  id: number;
  primaryTypeId: number;
  code: string; // pl. 'hu_gimnaziumi'
  nameKey: string;
  sortOrder: number;
  isActive: boolean;
}

// -------- Group types --------

export interface GroupType {
  id: number;
  countryCode: CountryCode;
  code: string;
  nameKey: string;
  suggestedForPrimaryTypeId: number | null;
  sortOrder: number;
  isActive: boolean;
}

// -------- Users + roles --------

export type ScopeType = 'system' | 'country' | 'institution' | 'group';

export type Role =
  | 'systemadmin'
  | 'applicationadmin'
  | 'country_admin'
  | 'institution_admin'
  | 'pta_leader'
  | 'pta_cashier'
  | 'parent'
  | 'student'
  | 'b2b_supplier';

export const ALL_ROLES: readonly Role[] = [
  'systemadmin',
  'applicationadmin',
  'country_admin',
  'institution_admin',
  'pta_leader',
  'pta_cashier',
  'parent',
  'student',
  'b2b_supplier',
] as const;

export const ALL_SCOPE_TYPES: readonly ScopeType[] = [
  'system',
  'country',
  'institution',
  'group',
] as const;

export interface User {
  id: number;
  email: string;
  displayName: string;
  address: string | null;
  phone: string | null;
  notes: string | null;
  authProvider: string;
  authSubject: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Egy user biztonsági szempontból szükséges részhalmaza — JWT-ben, audit log-ban,
 * error log-ban ezt használjuk. Nincs PII, nincs auth subject.
 */
export interface UserPublic {
  id: number;
  email: string;
  displayName: string;
  isActive: boolean;
}

export interface UserRole {
  id: number;
  userId: number;
  scopeType: ScopeType;
  scopeId: string | null;
  role: Role;
  validFrom: string;
  validTo: string | null;
  grantedBy: number | null;
}

/**
 * Scope referencia egy művelethez — a runtime permission check ezt használja.
 */
export interface ScopeRef {
  type: ScopeType;
  id: string | null;
}

// -------- Institution binding status (2026-04-26) --------

export type InstitutionBindingStatus = 'free_text' | 'claimed' | 'verified';

export const ALL_INSTITUTION_BINDING_STATUSES: readonly InstitutionBindingStatus[] = [
  'free_text',
  'claimed',
  'verified',
] as const;

// -------- Identity status (2026-04-26) --------

export type IdentityStatus = 'informal' | 'identified' | 'verified';

export const ALL_IDENTITY_STATUSES: readonly IdentityStatus[] = [
  'informal',
  'identified',
  'verified',
] as const;

// -------- Institutions --------

export interface Institution {
  id: number;
  countryCode: CountryCode;
  primaryTypeId: number;
  officialId: string;
  name: string;
  shortName: string | null;
  addressLine: string | null;
  city: string | null;
  postalCode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  notes: string | null;
  isActive: boolean;
  secondaryTypeIds: number[];
  createdAt: string;
  updatedAt: string;
}

export interface InstitutionCreateInput {
  countryCode: CountryCode;
  primaryTypeId: number;
  officialId: string;
  name: string;
  shortName?: string | null;
  addressLine?: string | null;
  city?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  notes?: string | null;
  secondaryTypeIds?: number[];
}

export type InstitutionUpdateInput = Partial<Omit<InstitutionCreateInput, 'countryCode'>>;

// -------- Groups --------

export interface Group {
  id: number;
  /** Lehet null, ha institutionBindingStatus = 'free_text' — ekkor institutionFreeText hordozza az iskola nevét. */
  institutionId: number | null;
  /** Csoport bekötöttsége a törzsadat-intézményhez. */
  institutionBindingStatus: InstitutionBindingStatus;
  /** free_text esetén a felhasználó által begépelt iskolanév. claimed/verified esetén null. */
  institutionFreeText: string | null;
  schoolYearId: number;
  groupTypeId: number;
  name: string;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Csoport-létrehozás. Két út:
 *  - **free_text**: `institutionBindingStatus='free_text'` + `institutionFreeText` kötelező,
 *    `institutionId` ne legyen megadva. Open-flow, bármely auth user használhatja.
 *  - **claimed/verified**: `institutionId` kötelező. Admin role-os út (vagy később claim-flow).
 *
 * A BE eldönti a payload alapján, melyik út érvényes és a megfelelő role-ellenőrzést futtatja.
 */
export interface GroupCreateInput {
  /** claimed/verified út. */
  institutionId?: number | null;
  /** free_text út — a felhasználó által beírt iskolanév. */
  institutionFreeText?: string | null;
  /** Default: ha institutionId van, 'verified'; ha institutionFreeText van, 'free_text'. */
  institutionBindingStatus?: InstitutionBindingStatus;
  schoolYearId: number;
  groupTypeId: number;
  name: string;
  notes?: string | null;
}

export type GroupUpdateInput = Partial<
  Pick<GroupCreateInput, 'schoolYearId' | 'groupTypeId' | 'name' | 'notes' | 'institutionFreeText'>
>;

/**
 * Egy csoport a `GET /me/groups` válaszban: a `Group` szükséges mezői + a feloldó megjelenítési
 * címke (`institutionLabel`), amit a FE a GroupPicker / dashboard / breadcrumb-okban használ.
 *
 * `institutionLabel` feloldása szerveroldalon:
 *   - claimed/verified bekotés → `institutions.name`
 *   - free_text             → `groups.institution_free_text`
 *   - egyébként           → null (nem előforduló)
 */
export interface MyGroupEntry extends Group {
  institutionLabel: string | null;
}

// -------- Cashier grants (időszakos pta_cashier role-grant) --------

/**
 * Egy meglevő user_roles sor a kapcsolódó user metaadatokkal kibővítve — a leader/admin
 * UI-ja ezt látja a listában.
 */
export interface CashierGrant {
  /** A user_roles.id, ezzel hivatkozunk PATCH/DELETE-nél. */
  id: number;
  userId: number;
  userEmail: string;
  userDisplayName: string;
  groupId: number;
  validFrom: string;       // ISO timestamp
  validTo: string | null;  // ISO timestamp; null = nyitott
  grantedBy: number | null;
  /** A teljes user_role rekord audit/UI számára (scope_type='group', role='pta_cashier'). */
  scopeType: ScopeType;    // mindig 'group'
  scopeId: string;         // a groupId stringként
  /** Le van-e járva a sor most (valid_to <= now())? FE convenience. */
  isExpired: boolean;
  /** A sor még érvénybe sem lépett (valid_from > now())? FE convenience. */
  isFuture: boolean;
}

export interface CashierGrantInput {
  /** A grantelendő user id. A leader saját magának is grantelhet. */
  userId: number;
  /** Default: now(). */
  validFrom?: string | null;
  /** Default: null (nyitott). */
  validTo?: string | null;
}

export interface CashierGrantUpdateInput {
  /** Új valid_to. Null érték = nyitottra váltás; ISO timestamp = lejárati idő. */
  validTo: string | null;
}

/**
 * Egy user-keresés találat — `GET /users/search?q=...&groupId=...` válasza.
 */
export interface UserSearchHit {
  id: number;
  email: string;
  displayName: string;
  isActive: boolean;
}

// -------- Cash balances (group nyitóegyenleg) --------

/**
 * Egy csoport jelenlegi pénz-egyenlege a primary currency-ben.
 * UNIQUE(group_id) — egy csoport, egy rekord. Átszerkesztés helyben.
 */
export interface CashBalance {
  id: number;
  groupId: number;
  currency: string;
  /** NUMERIC(14,2) string. */
  cashAmount: string;
  /** NUMERIC(14,2) string. */
  bankAmount: string;
  asOfDate: string;
  notes: string | null;
  createdBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CashBalanceUpsertInput {
  cashAmount: string;
  bankAmount: string;
  asOfDate: string;
  notes?: string | null;
}

// -------- Dashboard --------

/**
 * Csoport-szintű dashboard aggregátum (`GET /groups/:id/dashboard`).
 *
 * Csak a most számolható mezőket tartalmazza:
 *   - diák-statisztika (aktív tagok, identity_status bontás)
 *   - előírások státusz szerint
 *   - nyitott (published) követelés primary currency-ben
 *   - kassza-egyenleg (cash + bank, primary currency)
 *   - prognozált egyenleg = kassza + nyitott követelés
 *   - currency config (primary + opcionálisan secondary tájékoztató árfolyammal)
 *
 * A később hozzáadódó mezők (befizetés-státusz, késedelem) az F2e/F2d után
 * bővíthetők.
 */
export interface GroupDashboard {
  groupId: number;
  generatedAt: string;

  currencyConfig: {
    /** A csoport elsodleges currency-je — minden belső összeg ebben van. */
    primary: string;
    /** Opcionális tájékoztató currency. Null, ha nincs beállítva. */
    secondary: string | null;
    /**
     * 1 primary = `secondaryRate` secondary. NUMERIC(18,8) string.
     * Null, ha secondary be van állítva, de nincs érvényes árfolyam adat.
     */
    secondaryRate: string | null;
  };

  students: {
    activeCount: number;
    byIdentityStatus: Record<IdentityStatus, number>;
  };

  requirements: {
    byStatus: Record<PaymentRequirementStatus, number>;
  };

  /**
   * Kassza-egyenleg primary currency-ben. Null, ha még nem rögzítették.
   */
  cashBalance: {
    cashAmount: string;
    bankAmount: string;
    /** cashAmount + bankAmount, BE-n számolva. */
    total: string;
    asOfDate: string;
    notes: string | null;
  } | null;

  /**
   * Pénzmozgások aggregátuma a csoportra (primary currency-ben).
   * `currentBalance` = (cashBalance.total ‗vagy 0‘) + paymentsTotal − expensesTotal.
   * Ez a "számolt aktuális egyenleg" — nem azonos a kassza-nyitóval.
   */
  finance: {
    paymentsTotal: string;
    expensesTotal: string;
    currentBalance: string;
  };

  /**
   * Nyitott (published) követelés a primary currency-ben.
   * - outstandingTotal: csak NEM waived target-ek expectedAmount sum.
   * - forecastTotal: cashBalance.total + outstandingTotal (ha nincs cashBalance, csak outstanding).
   */
  outstanding: {
    requirementCount: number;
    targetCount: number;
    billableTargetCount: number;
    outstandingTotal: string;
    forecastTotal: string;
  };
}

/**
 * Dashboard idősor — `GET /groups/:id/dashboard/timeseries?range=7d|30d|90d|1y`.
 *
 * Egy csoport pénzügyi állapotának időbeli alakulása primary currency-ben
 * (multi-currency F3+):
 *   - balance: a csoport számolt aktuális egyenlege a bucket végén
 *     (cashBalance.total + Σ(payments) − Σ(expenses) az időszak alatt)
 *   - openReceivables: a bucket végén nyitott (published) követelés összege
 *     (target.expectedAmount − Σ(payments addig), ha publishedAt ≤ bucketEnd
 *     és !closedAt vagy closedAt > bucketEnd, csak !waived target-ek)
 *
 * Bucket-granularitás:
 *   - 7d, 30d → napi
 *   - 90d, 1y → heti (ISO hét, hétfő kezdetű)
 *
 * MVP: csak primary currency-ben mozgó payment/expense szerepel
 * (a többi currency aggregálását F3+-ban implementáljuk).
 */
export type DashboardTimeseriesRange = '7d' | '30d' | '90d' | '1y';

export const ALL_DASHBOARD_TIMESERIES_RANGES: readonly DashboardTimeseriesRange[] = [
  '7d',
  '30d',
  '90d',
  '1y',
] as const;

export type DashboardTimeseriesGranularity = 'daily' | 'weekly';

export interface DashboardTimeseriesBucket {
  /** A bucket kezdete ISO date (napi: maga a nap; heti: ISO hét hétfője). */
  bucketDate: string;
  /** NUMERIC string. Csoport-egyenleg a bucket VÉGÉN (running). */
  balance: string;
  /** NUMERIC string. Nyitott követelés a bucket VÉGÉN. */
  openReceivables: string;
  /** NUMERIC string. A bucket-en belüli payments összege (nem kumulatív). */
  paymentsInBucket: string;
  /** NUMERIC string. A bucket-en belüli expenses összege (nem kumulatív). */
  expensesInBucket: string;
}

export interface DashboardTimeseries {
  groupId: number;
  range: DashboardTimeseriesRange;
  granularity: DashboardTimeseriesGranularity;
  currency: string;
  generatedAt: string;
  /** Az időszak első bucket-jének dátuma. */
  fromDate: string;
  /** Az időszak utolsó bucket-jének dátuma. */
  toDate: string;
  /** A range előtti nyitó-egyenleg (cashBalance.total + Σ ezelőtti payments − Σ ezelőtti expenses). */
  openingBalance: string;
  buckets: DashboardTimeseriesBucket[];
}

// -------- Students --------

export interface Student {
  id: number;
  /** Lehet null, ha a diák free_text csoportban van (nincs törzsadat-intézmény). */
  institutionId: number | null;
  /** Magyar Oktatási azonosító, NULL ha identityStatus='informal'. */
  educationalId: string | null;
  identityStatus: IdentityStatus;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  addressLine: string | null;
  city: string | null;
  postalCode: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StudentCreateInput {
  /** Lehet null, ha a diák free_text csoportba kerül. */
  institutionId: number | null;
  /** Opcionális. Ha hiányzik, a diák 'informal' identityStatus-szal jön létre. */
  educationalId?: string | null;
  /** Default: 'informal' ha educationalId hiányzik, 'identified' ha jelen van. */
  identityStatus?: IdentityStatus;
  firstName: string;
  lastName: string;
  birthDate?: string | null;
  addressLine?: string | null;
  city?: string | null;
  postalCode?: string | null;
  notes?: string | null;
}

export type StudentUpdateInput = Partial<Omit<StudentCreateInput, 'institutionId'>>;

// -------- Group memberships --------

export interface GroupMembership {
  id: number;
  studentId: number;
  groupId: number;
  validFrom: string;
  validTo: string | null;
  reason: string | null;
  createdAt: string;
}

// -------- Lifecycle (F1f) --------

/**
 * Csoport évfordulói előléptetése.
 *
 * Két mód:
 *  - `create` — új `groups` rekord készül a cél tanévben, ugyanabban az intézményben.
 *    A `targetName` és `targetGroupTypeId` a forrás csoport értékeit veszi, ha nincs megadva.
 *  - `assign` — a diákok egy már létező `targetGroupId` csoportba kerülnek.
 *
 * Mindkét módban: a forrás csoport összes nyitott membership-je lezárul `effectiveDate`-tel,
 * és új membership nyit az új csoporthoz `effectiveDate`-ről.
 *
 * `effectiveDate` opcionális: ha nincs, a cél csoport tanévének `start_date`-jét használjuk.
 */
export interface AdvanceGroupInput {
  mode: 'create' | 'assign';
  // mode='create':
  targetSchoolYearId?: number;
  targetName?: string;
  targetGroupTypeId?: number;
  // mode='assign':
  targetGroupId?: number;
  // közös:
  effectiveDate?: string; // ISO date
  deactivateOldGroup?: boolean; // default: true
  reason?: string; // default: 'year_advance'
}

export interface AdvanceGroupResult {
  sourceGroupId: number;
  targetGroup: Group;
  movedStudentCount: number;
  closedMembershipIds: number[];
  createdMembershipIds: number[];
  sourceGroupDeactivated: boolean;
}

/**
 * Egy diák áthelyezése másik csoportba.
 * A diák összes nyitott membership-je lezárul `effectiveDate`-tel, és egy új
 * nyit a cél csoportba.
 *
 * Megjegyzés: a cél csoport lehet másik intézményben — ekkor a diák
 * `institution_id` mezője is frissül.
 */
export interface TransferStudentInput {
  targetGroupId: number;
  effectiveDate?: string; // default: today
  reason?: string; // default: 'transfer'
}

export interface TransferStudentResult {
  studentId: number;
  targetGroupId: number;
  newInstitutionId: number | null; // null, ha nem változott
  closedMembershipIds: number[];
  createdMembershipId: number;
}

/**
 * Diák kilépése a rendszerből. A diák összes nyitott membership-je lezárul,
 * és opcionálisan maga a diák is deaktiválódik.
 */
export interface LeaveStudentInput {
  effectiveDate?: string; // default: today
  deactivateStudent?: boolean; // default: true
  reason?: string; // default: 'leave'
}

export interface LeaveStudentResult {
  studentId: number;
  closedMembershipIds: number[];
  studentDeactivated: boolean;
}

// -------- Audit + error logs (F1g) --------

/**
 * Audit esemény. Minden CRUD és lifecycle művelet egy sort ír.
 * A `delta_json` szerveroldalon a TÉNYLEGESEN megtörtént változást tartalmazza
 * (before / after / changed) — nem a kliens által küldött payload.
 */
export interface AuditEvent {
  id: number;
  serverTs: string; // ISO timestamp
  clientTs: string | null;
  userId: number | null;
  action: string; // pl. 'institution.create', 'group.update', 'student.transfer'
  entityType: string; // pl. 'institution', 'group', 'student', 'membership'
  entityId: string | null;
  scopeType: ScopeType;
  scopeId: string | null;
  deltaJson: AuditDelta | null;
  ip: string | null;
  userAgent: string | null;
}

/**
 * Az audit log `delta_json` formátuma.
 *  - CREATE: { after }
 *  - UPDATE: { before, after, changed: { field: {from, to} } }
 *  - DELETE: { before }
 *  - LIFECYCLE: { ... művelet-specifikus mezők }
 */
export interface AuditDelta {
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  changed?: Record<string, { from: unknown; to: unknown }>;
  // Szabadon bővíthető művelet-specifikus mezőkkel (lifecycle-hez).
  [key: string]: unknown;
}

export type ErrorSource = 'BE' | 'FE' | 'FEA' | 'FEB';
export type ErrorSeverity = 'fatal' | 'error' | 'warn' | 'info';

export const ALL_ERROR_SOURCES: readonly ErrorSource[] = ['BE', 'FE', 'FEA', 'FEB'] as const;
export const ALL_ERROR_SEVERITIES: readonly ErrorSeverity[] = [
  'fatal',
  'error',
  'warn',
  'info',
] as const;

export interface ErrorLogEntry {
  id: number;
  fingerprint: string;
  source: ErrorSource;
  severity: ErrorSeverity;
  userId: number | null;
  message: string;
  stack: string | null;
  contextJson: Record<string, unknown> | null;
  url: string | null;
  userAgent: string | null;
  ip: string | null;
  firstSeen: string; // ISO timestamp
  lastSeen: string;
  count: number;
}

/**
 * FE-k által küldött hibajelentés payload (`POST /errors/report`).
 * `source` csak 'FE' | 'FEA' | 'FEB' lehet — a 'BE'-t a BE maga logolja.
 */
export interface ErrorReportInput {
  source: 'FE' | 'FEA' | 'FEB';
  severity?: ErrorSeverity; // default: 'error'
  message: string;
  stack?: string;
  context?: Record<string, unknown>;
  url?: string;
  clientTs?: string; // ISO timestamp, FE időbélyeg
}

export interface ErrorReportResult {
  fingerprint: string;
  count: number; // a dedup utáni aktuális érték
  firstSeen: string;
  lastSeen: string;
}

// -------- i18n (F1h) --------

export type I18nLanguage = string; // 'hu' | 'en' | ... szabadon bővíthető
export type I18nNamespace = 'common' | 'refdata' | 'fe' | 'fea' | 'feb';

export const ALL_I18N_NAMESPACES: readonly I18nNamespace[] = [
  'common',
  'refdata',
  'fe',
  'fea',
  'feb',
] as const;

/**
 * Egy DB-ben tárolt fordítás-override. A JSON default-okat írja felül runtime-ban.
 */
export interface Translation {
  id: number;
  language: I18nLanguage;
  namespace: I18nNamespace;
  key: string;
  value: string;
  updatedBy: number | null;
  updatedAt: string; // ISO
  createdAt: string;
}

/**
 * `GET /i18n/:language` válasz. Egyszerű map: namespace → (key → value).
 * Csak a DB-ben tárolt override-okat tartalmazza.
 */
export type I18nBundle = Record<string, Record<string, string>>;

/**
 * `PUT /i18n/:language/:namespace/:key` request body.
 */
export interface TranslationUpsertInput {
  value: string;
}

// -------- Admin logs (F1i) --------

/**
 * Közös list-response forma a log endpointokhoz: paginált sorok + teljes szám
 * a szűrőre (paging UI-hoz).
 */
export interface PagedResult<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/** `GET /admin/errors` query paraméterek. */
export interface ErrorLogsQuery {
  source?: ErrorSource;
  severity?: ErrorSeverity;
  fingerprint?: string;
  userId?: number;
  textSearch?: string; // a message-ben és url-ben keres (ILIKE)
  from?: string;       // ISO timestamp — last_seen >= from
  to?: string;         // ISO timestamp — first_seen <= to
  limit?: number;      // default 50, max 500
  offset?: number;     // default 0
}

/** `GET /admin/audit` query paraméterek. */
export interface AuditEventsQuery {
  userId?: number;
  action?: string;
  entityType?: string;
  entityId?: string;
  scopeType?: ScopeType;
  scopeId?: string;
  textSearch?: string; // action+entity_type+delta_json text-search
  from?: string;       // ISO timestamp — server_ts >= from
  to?: string;         // ISO timestamp — server_ts <= to
  limit?: number;
  offset?: number;
}

/** `DELETE /admin/errors` / `DELETE /admin/audit` batch törlés input. */
export interface LogBulkDeleteInput {
  /** Csak az ennyi idő előtti rekordokat törli. Kötelező (nincs "törölj mindent"). */
  before: string; // ISO timestamp
  /** Opcionális szűrők — ezek megőrződnek a törlésnél is. */
  source?: ErrorSource;
  severity?: ErrorSeverity;
  fingerprint?: string;
}

export interface LogBulkDeleteResult {
  deleted: number;
}

// -------- Finance: legal profiles + tax profiles (F2a) --------

/**
 * Pénzkezelési módok. A jogi profilok allowed_payment_methods mezője ezt a halmazt használja.
 */
export type PaymentMethod = 'cash' | 'bank_transfer' | 'card' | 'escrow';

export const ALL_PAYMENT_METHODS: readonly PaymentMethod[] = [
  'cash',
  'bank_transfer',
  'card',
  'escrow',
] as const;

export type LegalEntityType = 'foundation' | 'association' | 'other';

export const ALL_LEGAL_ENTITY_TYPES: readonly LegalEntityType[] = [
  'foundation',
  'association',
  'other',
] as const;

/**
 * Adózási profil — F2-ben csak címke és leírás, automatikus adószámítás nincs.
 * A mezőkészlet F3+-ban bővíthető (ÁFA határ, ajándékozási küszöb, stb.).
 */
export interface TaxProfile {
  id: number;
  countryCode: CountryCode;
  code: string;          // pl. 'hu_none', 'hu_foundation'
  nameKey: string;       // i18n
  descriptionKey: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Jogi profil — a PTA pénzkezelésének konfigurálható jogi kerete.
 * Spec §6.2. Ország-szintű törzsadat, intézményhez és/vagy csoporthoz rendelhető.
 */
export interface LegalProfile {
  id: number;
  countryCode: CountryCode;
  code: string;
  nameKey: string;
  descriptionKey: string;

  allowedPaymentMethods: PaymentMethod[];

  requiresReceiptForExpense: boolean;
  requiresInvoiceForExpense: boolean;

  requiresDualApproval: boolean;
  /** Legkisebb pénzegységben (HUF: fillér). Null = nincs küszöb. */
  dualApprovalThresholdMinorUnit: number | null;

  requiresAnnualReport: boolean;
  annualReportDocumentKeys: string[]; // i18n kulcsok

  hasLegalEntity: boolean;
  legalEntityType: LegalEntityType | null;

  taxProfileId: number | null;

  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Intézmény-szintű jogi konfig. Történeti (valid_from/valid_to).
 */
export interface InstitutionLegalConfig {
  id: number;
  institutionId: number;
  legalProfileId: number;
  validFrom: string; // ISO date
  validTo: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: number | null;
}

/**
 * Csoport-szintű felülbirálás. Ha nincs nyitott sor, az intézmény-szintű konfig érvényes.
 */
export interface GroupLegalConfigOverride {
  id: number;
  groupId: number;
  legalProfileId: number;
  validFrom: string;
  validTo: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: number | null;
}

/**
 * A `getLegalConfig(institutionId, groupId, atDate)` helper eredménye.
 *  - `source` megmutatja, honnan jött a konfig (override csoporttul, vagy intézménytől).
 *  - `profile` a teljes jogi profil, kibővített adózási profillal.
 *  - `configRowId` annak a sornak az id-ja (institution_legal_config vagy group_override),
 *    ami jelenleg adja a konfigót — audit log és UI-n érvényesség kijelzéséhez.
 */
export interface ResolvedLegalConfig {
  source: 'group_override' | 'institution' | 'none';
  configRowId: number | null;
  profile: LegalProfile | null;
  taxProfile: TaxProfile | null;
  validFrom: string | null;
  validTo: string | null;
}

// CRUD input-ok

export interface LegalProfileCreateInput {
  countryCode: CountryCode;
  code: string;
  nameKey: string;
  descriptionKey: string;
  allowedPaymentMethods: PaymentMethod[];
  requiresReceiptForExpense?: boolean;
  requiresInvoiceForExpense?: boolean;
  requiresDualApproval?: boolean;
  dualApprovalThresholdMinorUnit?: number | null;
  requiresAnnualReport?: boolean;
  annualReportDocumentKeys?: string[];
  hasLegalEntity?: boolean;
  legalEntityType?: LegalEntityType | null;
  taxProfileId?: number | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type LegalProfileUpdateInput = Partial<Omit<LegalProfileCreateInput, 'countryCode' | 'code'>>;

export interface TaxProfileCreateInput {
  countryCode: CountryCode;
  code: string;
  nameKey: string;
  descriptionKey: string;
  sortOrder?: number;
  isActive?: boolean;
}

export type TaxProfileUpdateInput = Partial<Omit<TaxProfileCreateInput, 'countryCode' | 'code'>>;

/**
 * Intézményhez vagy csoporthoz jogi profil hozzárendelése.
 * A BE a korábbi nyitott sort lezárja `validFrom - 1 day`-jel és új sort nyít.
 */
export interface AssignLegalProfileInput {
  legalProfileId: number;
  validFrom: string; // ISO date
  notes?: string | null;
}

// -------- Finance: currency + system_settings + exchange rates + payment requirements (F2b) --------

/**
 * F2b: currency törzs. Új currency-t FEA-ról systemadmin vehet fel.
 */
export interface Currency {
  /** 3-karakteres currency kód (ISO 4217), pl. 'HUF'. */
  code: string;
  /** i18n kulcs a refdata namespace-ben. */
  nameKey: string;
  /** Tizedes jegyek száma (HUF: 0, EUR/USD: 2). */
  minorUnit: number;
  /** Megjelenítési szimbólum, pl. 'Ft', '€'. Nullable. */
  symbol: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CurrencyCreateInput {
  code: string;
  nameKey: string;
  minorUnit?: number;
  symbol?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type CurrencyUpdateInput = Partial<Omit<CurrencyCreateInput, 'code'>>;

/**
 * F2b: scope-os key-value beállítás.
 * Scope-hierarchia: global > country > institution > group. Feloldás alulról felfelé,
 * első találat nyer.
 */
export type SystemSettingScopeType = 'global' | 'country' | 'institution' | 'group';

export const ALL_SYSTEM_SETTING_SCOPE_TYPES: readonly SystemSettingScopeType[] = [
  'global',
  'country',
  'institution',
  'group',
] as const;

export interface SystemSetting {
  id: number;
  scopeType: SystemSettingScopeType;
  /** 'global' esetén null, egyébként string (country code vagy id). */
  scopeId: string | null;
  key: string;
  value: unknown; // JSONB — hívó parsolja a saját típusára
  createdBy: number | null;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SystemSettingUpsertInput {
  scopeType: SystemSettingScopeType;
  scopeId: string | null;
  key: string;
  value: unknown;
}

/**
 * Egy key feloldott értéke — melyik scope-ból jött, és a tényleges value.
 */
export interface ResolvedSystemSetting<T = unknown> {
  key: string;
  value: T | null;
  /** A scope, ahonnan a value jött. `null` ha nincs sehol definiálva. */
  source: SystemSettingScopeType | null;
  sourceScopeId: string | null;
}

/**
 * Ismert system_settings kulcsok. Nem kényszerítő enum, csak konvenció.
 */
export const SystemSettingKeys = {
  /** Másodlagos (tájékoztató) deviza: 3-betűs currency code string. */
  SecondaryCurrency: 'finance.secondary_currency',
  /** Másodlagos deviza árfolyam forrása: 'mnb' | 'ecb' | 'manual'. Általában global scope. */
  SecondaryCurrencySource: 'finance.secondary_currency_source',
} as const;

/**
 * F2b: exchange rate. SZIGORÚAN TÁJÉKOZTATÓ — semmilyen pénzügyi validációban nem vesz részt.
 */
export type ExchangeRateSource = 'mnb' | 'ecb' | 'manual';

export const ALL_EXCHANGE_RATE_SOURCES: readonly ExchangeRateSource[] = [
  'mnb',
  'ecb',
  'manual',
] as const;

export interface ExchangeRate {
  id: number;
  /** 1 baseCurrency = rate quoteCurrency. */
  baseCurrency: string;
  quoteCurrency: string;
  /** Stringben tartjuk, mert NUMERIC(18,8) — precizitásra érzékeny. */
  rate: string;
  source: ExchangeRateSource;
  validFrom: string; // ISO date
  validTo: string | null;
  createdBy: number | null;
  createdAt: string;
}

export interface ExchangeRateManualInput {
  baseCurrency: string;
  quoteCurrency: string;
  rate: string;
  validFrom: string; // ISO date
}

/**
 * F2b: befizetési előírás. Egy csoport pénztárosa hozza létre.
 * Állapot-gép: draft → published → (cancelled | closed).
 */
export type PaymentRequirementStatus = 'draft' | 'published' | 'cancelled' | 'closed';

export const ALL_PAYMENT_REQUIREMENT_STATUSES: readonly PaymentRequirementStatus[] = [
  'draft',
  'published',
  'cancelled',
  'closed',
] as const;

export interface PaymentRequirement {
  id: number;
  groupId: number;
  title: string;
  description: string | null;
  purpose: string;
  /** NUMERIC(14,2), stringként tartjuk a precizitás miatt. */
  amount: string;
  currency: string;
  dueDate: string | null;
  status: PaymentRequirementStatus;
  publishedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  closedAt: string | null;
  createdBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentRequirementTarget {
  id: number;
  requirementId: number;
  studentId: number;
  /** NUMERIC(14,2), stringként. */
  expectedAmount: string;
  waived: boolean;
  waiveReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Egy requirement a target-ekkel együtt — FEA / FE listázáshoz.
 */
export interface PaymentRequirementWithTargets extends PaymentRequirement {
  targets: PaymentRequirementTarget[];
}

/**
 * Egy target + a kapcsolódó requirement alapadatok — szülői view-hoz.
 */
export interface PaymentRequirementForParent {
  requirement: PaymentRequirement;
  target: PaymentRequirementTarget;
  studentId: number;
  studentFirstName: string;
  studentLastName: string;
  groupId: number;
  groupName: string;
  institutionId: number;
  institutionName: string;
}

/**
 * CRUD inputok.
 */
export interface PaymentRequirementCreateInput {
  groupId: number;
  title: string;
  description?: string | null;
  purpose: string;
  amount: string;
  currency: string;
  dueDate?: string | null;
  /**
   * Opcionális target override lista. Ha null/üres, a BE automatikusan
   * létrehozza a csoport aktív tagjainak a targeteket az `amount` értékkel.
   */
  targets?: Array<{
    studentId: number;
    expectedAmount?: string; // ha nincs, az amount-ot használja
    waived?: boolean;
    waiveReason?: string | null;
  }> | null;
}

export interface PaymentRequirementUpdateInput {
  title?: string;
  description?: string | null;
  purpose?: string;
  amount?: string;
  currency?: string;
  dueDate?: string | null;
  updateMode?: 'amount_only' | 'full';
}

export interface PaymentRequirementPublishInput {
  /** Ha nincs megadva, `now()`. */
  publishedAt?: string;
}

export interface PaymentRequirementCancelInput {
  cancelReason: string;
}

export interface PaymentRequirementTargetUpsertInput {
  studentId: number;
  expectedAmount: string;
  waived?: boolean;
  waiveReason?: string | null;
}

export interface PaymentRequirementTargetUpdateInput {
  expectedAmount?: string;
  waived?: boolean;
  waiveReason?: string | null;
}

/**
 * F2c: hasonló korábbi előírás javaslat (pg_trgm trigram similarity).
 * Egy találat elég adatot tartalmaz, hogy a FE előtölthesse a `purpose`,
 * `description`, `currency` mezőket az új előírás form-ba.
 */
export interface SimilarRequirementHit {
  id: number;
  groupId: number;
  groupName: string;
  /** institution feloldott label — bound → institutions.name; free_text → institution_free_text; null ha nincs. */
  institutionLabel: string | null;
  title: string;
  purpose: string;
  description: string | null;
  amount: string;
  currency: string;
  status: PaymentRequirementStatus;
  /** A FE-n megjelenítendő időpont: publishedAt > closedAt > createdAt. ISO timestamp. */
  occurredAt: string;
  /** 0..1 hasonlóság (pg_trgm `similarity()`). */
  similarity: number;
}

// -------- F2e/F2f: payments + expenses --------

/**
 * Konkrét befizetés egy target-re. Részfizetés engedett — több payment
 * mutathat ugyanarra a target_id-ra. A target "teljesítve" akkor, ha
 * `SUM(payments.amount) >= target.expectedAmount`.
 *
 * `currency` mindig a csoport primary currency-je (multi-currency F3+).
 */
export interface Payment {
  id: number;
  targetId: number;
  groupId: number;
  /** NUMERIC(14,2) string. */
  amount: string;
  currency: string;
  paidAt: string; // ISO date YYYY-MM-DD
  paymentMethod: PaymentMethod;
  /**
   * Külső hivatkozás, pl. banki tranzakció azonosító a későbbi CSV/bank import
   * fele — manual rögzítéskor null. Egyelőre csak tároljuk.
   */
  externalRef: string | null;
  notes: string | null;
  recordedBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentCreateInput {
  targetId: number;
  amount: string;
  paidAt: string;
  paymentMethod: PaymentMethod;
  externalRef?: string | null;
  notes?: string | null;
}

export interface PaymentUpdateInput {
  amount?: string;
  paidAt?: string;
  paymentMethod?: PaymentMethod;
  externalRef?: string | null;
  notes?: string | null;
}

/**
 * Egy target a hozzá tartozó payments összegével — detail-page-en mutatjuk.
 */
export interface TargetWithPayments {
  target: PaymentRequirementTarget;
  payments: Payment[];
  /** SUM(payments.amount) NUMERIC(14,2) string. */
  paidTotal: string;
  /** expectedAmount - paidTotal NUMERIC(14,2) string. Negatív lehet (túl van fizetve). */
  remaining: string;
  /** paidTotal >= expectedAmount && !waived. */
  fullyPaid: boolean;
}

/**
 * Csoport-szintű kiadás. Minden expense-nek van `purpose` — ha `requirement_id`
 * van, az előírás céljához köthető (pl. kirándulás buszbérlés); ha null,
 * az általános működési kiadás saját `purpose`-szal (pl. "irodaszer").
 *
 * `invoice_number` a működt jegy/számla száma. Fájl-feltöltés + PDF generálás
 * F2f/2 fázis. A csoport `finance.expense_receipt_required` system_setting
 * dönti el, hogy a form kötelezővé teszi-e — default: true.
 */
export interface Expense {
  id: number;
  groupId: number;
  /** Null = általános működési kiadás; non-null = cél-kötött előíráshoz. */
  requirementId: number | null;
  purpose: string;
  /** NUMERIC(14,2) string. */
  amount: string;
  currency: string;
  spentAt: string; // ISO date
  paymentMethod: PaymentMethod;
  /** Akinek fizettünk (számlázó vagy személy). Nullable. */
  payeeName: string | null;
  /** Számla / nyugta száma. Nullable, de a csoport receipt-required beállításától függően lehet kötelező a form-on. */
  invoiceNumber: string | null;
  notes: string | null;
  recordedBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseCreateInput {
  requirementId?: number | null;
  purpose: string;
  amount: string;
  spentAt: string;
  paymentMethod: PaymentMethod;
  payeeName?: string | null;
  invoiceNumber?: string | null;
  notes?: string | null;
}

export interface ExpenseUpdateInput {
  requirementId?: number | null;
  purpose?: string;
  amount?: string;
  spentAt?: string;
  paymentMethod?: PaymentMethod;
  payeeName?: string | null;
  invoiceNumber?: string | null;
  notes?: string | null;
}

// -------- Reports (riport oldal) --------

/**
 * Riport oldal — több szempontú lekérdezés egy csoporton belül.
 *
 * Három view:
 *  - `by-student` — diákonkénti összegzés (várt / fizetett / hátralék / státusz).
 *  - `by-requirement` — előírásonkénti összegzés (várt / fizetett / target-bontás).
 *  - `by-method` — fizetési mód szerinti aggregátum (cash / bank_transfer / card / escrow).
 *
 * Sor-szintű státusz a NEM waived target-eken számolva:
 *  - `paid` — minden NEM waived target teljesítve (paid >= expected).
 *  - `partial` — van legalább egy NEM waived target, ahol 0 < paid < expected,
 *    VAGY van paid és van unpaid is.
 *  - `unpaid` — minden NEM waived target paid = 0.
 *  - `waived` — nincs NEM waived target (minden waived).
 *
 * Filter szemantika:
 *  - paidFrom/paidTo: payments.paid_at szűrés — csak az ezen időszakban befizetett
 *    payments számítanak a paidTotal-ba.
 *  - dueFrom/dueTo: requirement.due_date szűrés — csak az ezen időszakban
 *    esedékes előírások jelennek meg (a NULL dueDate ki van zárva, ha bármelyik
 *    határ meg van adva).
 *  - paymentMethod: by-student / by-requirement view-n a paidTotal-t leszűkíti
 *    az ezzel a módszerrel rögzített payments-re. by-method view-n nincs hatása.
 *  - status: row-level filter — drop rows whose computed status !== filter.
 */
export type ReportView = 'by-student' | 'by-requirement' | 'by-method';

export const ALL_REPORT_VIEWS: readonly ReportView[] = [
  'by-student',
  'by-requirement',
  'by-method',
] as const;

export type ReportRowStatus = 'paid' | 'partial' | 'unpaid' | 'waived';

export const ALL_REPORT_ROW_STATUSES: readonly ReportRowStatus[] = [
  'paid',
  'partial',
  'unpaid',
  'waived',
] as const;

export interface ReportFilters {
  paidFrom?: string | null;
  paidTo?: string | null;
  dueFrom?: string | null;
  dueTo?: string | null;
  paymentMethod?: PaymentMethod | null;
  status?: ReportRowStatus | null;
}

/** Egy diák egy csoporton belüli aggregátuma. */
export interface ReportByStudentRow {
  studentId: number;
  firstName: string;
  lastName: string;
  identityStatus: IdentityStatus;
  /** SUM(NEM waived target.expectedAmount) a szűrt requirement-halmazon. */
  expectedTotal: string;
  /** SUM(payments.amount) a NEM waived target-eken (filter-szabottan). */
  paidTotal: string;
  /** expectedTotal - paidTotal. Negatív lehet, ha túlfizetés. */
  remaining: string;
  /** Hány target tartozik a diákhoz a szűrt requirement-halmazban. */
  targetCount: number;
  /** Ezekből hány waived. */
  waivedCount: number;
  status: ReportRowStatus;
}

/** Egy előírás (requirement) aggregátuma. */
export interface ReportByRequirementRow {
  requirementId: number;
  title: string;
  purpose: string;
  status: PaymentRequirementStatus;
  dueDate: string | null;
  /** SUM(NEM waived target.expectedAmount). */
  expectedTotal: string;
  /** SUM(payments.amount) az előíráshoz tartozó NEM waived target-eken (filter-szabottan). */
  paidTotal: string;
  remaining: string;
  targetCount: number;
  paidTargetCount: number;
  partialTargetCount: number;
  unpaidTargetCount: number;
  waivedTargetCount: number;
  rowStatus: ReportRowStatus;
}

/** Fizetési mód szerinti aggregátum. */
export interface ReportByMethodRow {
  paymentMethod: PaymentMethod;
  paymentCount: number;
  /** SUM(payments.amount) ezen a módon (filter-szabottan). */
  paidTotal: string;
  /** Hány különböző target érintett. */
  targetCount: number;
  /** Hány különböző requirement érintett. */
  requirementCount: number;
}

/**
 * Közös válasz-burok — view megkülönböztetésre + audit-mezőknek.
 *
 * Kibővítve riport-fej (PDF/CSV header) generáláshoz szükséges metaadatokkal:
 *  - groupName: csoport neve, pl. "5.a"
 *  - institutionLabel: bound → institutions.name; free_text → institution_free_text; null
 *  - schoolYearDisplayName: pl. "2025/2026"
 *  - generatedBy: a riportot lekérő user (audit-célra)
 *  - currencySymbol + currencyMinorUnit + numberFormat: pénzformázáshoz a FE-n
 */
export interface ReportResponseBase {
  groupId: number;
  groupName: string;
  institutionLabel: string | null;
  schoolYearDisplayName: string;
  generatedAt: string;
  generatedBy: {
    id: number;
    displayName: string;
    email: string;
  };
  currency: string;
  /**
   * A `currency` szimbóluma (pl. 'Ft', '€', '$'), vagy null ha az adott
   * currency-hez nincs symbol — ekkor a FE/PDF maga a code-ot mutatja.
   */
  currencySymbol: string | null;
  /**
   * A `currency` minor unit-ja (HUF: 0, EUR: 2). Pénzformázáshoz a
   * decimals default-ja.
   */
  currencyMinorUnit: number;
  /**
   * A csoport országának számformázó beállítása. A FE pénzformázáshoz használja.
   */
  numberFormat: NumberFormat;
  filters: ReportFilters;
}

export interface ReportByStudentResponse extends ReportResponseBase {
  view: 'by-student';
  rows: ReportByStudentRow[];
  totals: {
    expectedTotal: string;
    paidTotal: string;
    remaining: string;
    studentCount: number;
  };
}

export interface ReportByRequirementResponse extends ReportResponseBase {
  view: 'by-requirement';
  rows: ReportByRequirementRow[];
  totals: {
    expectedTotal: string;
    paidTotal: string;
    remaining: string;
    requirementCount: number;
  };
}

export interface ReportByMethodResponse extends ReportResponseBase {
  view: 'by-method';
  rows: ReportByMethodRow[];
  totals: {
    paidTotal: string;
    paymentCount: number;
  };
}

export type ReportResponse =
  | ReportByStudentResponse
  | ReportByRequirementResponse
  | ReportByMethodResponse;
